import { useEffect, useState, useMemo } from "react";
import { api, inr } from "@/lib/api";
import { Plus, AlertCircle, ChevronRight, Image as ImageIcon, Pencil } from "lucide-react";
import SidePanel from "./SidePanel";
import PlanCardActions from "./PlanCardActions";

const today = () => new Date().toISOString().slice(0, 10);

function urgencyOf(scheduledDate, displayUntil) {
  const t = today();
  const cutoff = displayUntil || scheduledDate;
  if (!cutoff) return { cls: "bg-slate-100 text-slate-500", label: "—" };
  const diff = (new Date(cutoff) - new Date(t)) / (1000 * 60 * 60 * 24);
  if (diff < 0) return { cls: "bg-slate-200 text-slate-500", label: "Closed" };
  if (diff < 1) return { cls: "bg-rose-100 text-rose-700 animate-pulse", label: "< 24h" };
  if (diff < 3) return { cls: "bg-amber-100 text-amber-700", label: `${Math.ceil(diff)}d left` };
  return { cls: "bg-emerald-100 text-emerald-700", label: `${Math.ceil(diff)}d left` };
}

function dishNames(special) {
  if (Array.isArray(special.items) && special.items.length > 0) {
    return special.items.map(it => (typeof it === "string" ? it : it.name)).filter(Boolean);
  }
  return [];
}

function SpecialCard({ special, onClick, onEdit, onChanged }) {
  const urg = urgencyOf(special.scheduled_date, special.display_until);
  const fillPct = special.capacity ? Math.min(100, ((special.booked || 0) / special.capacity) * 100) : 0;
  const dishes = dishNames(special);
  const archived = !!special.archived;

  return (
    <div
      data-testid={`special-card-${special.id}`}
      className={`text-left border rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer ${archived ? "bg-rose-50/40 border-rose-200" : "bg-rose-50 border-rose-200"}`}
      onClick={onClick}
    >
      <div className="h-36 bg-rose-100 flex items-center justify-center relative">
        {special.image_url ? (
          <img src={special.image_url} alt="" className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <ImageIcon size={40} className="text-rose-300" />
        )}
        <span className={`absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${archived ? "bg-amber-200 text-amber-900" : urg.cls}`}>
          <AlertCircle size={11} /> {archived ? "Archived" : urg.label}
        </span>
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <button data-testid={`special-edit-${special.id}`} onClick={(e) => { e.stopPropagation(); onEdit(special); }}
            className="p-1.5 rounded-full bg-white/90 text-rose-900 hover:bg-white" title="Edit event">
            <Pencil size={11} />
          </button>
          <div className="bg-white/90 rounded-full">
            <PlanCardActions plan={special} resource="specials" accentClass="bg-white text-rose-900"
              onChanged={onChanged} testid="spec" />
          </div>
        </div>
        <span className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-900 text-white capitalize">
          {special.meal_slot || special.slot || "lunch"}
        </span>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="font-heading text-lg font-extrabold text-rose-900 tracking-tight">{special.name}</h3>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-rose-700 mt-0.5">{special.scheduled_date}</div>
          </div>
          <div className="text-xl font-extrabold text-rose-950">{inr(special.price || 0)}</div>
        </div>

        {dishes.length > 0 && (
          <div className="text-xs text-rose-900 mb-3 line-clamp-2 mt-2">
            {dishes.join(" · ")}
          </div>
        )}

        {special.capacity && (
          <div className="mb-2">
            <div className="flex items-center justify-between text-[10px] uppercase font-bold text-rose-800 tracking-wider mb-1">
              <span>Bookings</span><span>{special.booked || 0} / {special.capacity}</span>
            </div>
            <div className="h-1.5 bg-rose-200 rounded-full overflow-hidden">
              <div className="h-full bg-rose-600" style={{ width: `${fillPct}%` }} />
            </div>
          </div>
        )}
        <div className="text-[11px] text-rose-700 pt-2 border-t border-rose-200 flex items-center justify-between">
          <span>Expires: {special.display_until || special.scheduled_date}</span>
          <ChevronRight size={14} className="text-rose-600 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </div>
  );
}

function SpecialDetail({ special, onClose }) {
  const [tab, setTab] = useState("orders");
  const dishes = dishNames(special);

  // Group bookers by day — for now we show the single scheduled_date with booked count.
  // (Detailed per-student rows arrive via Transaction records; this is a forward-looking placeholder.)
  return (
    <SidePanel open={!!special} onClose={onClose} accent="rose" title={special?.name}>
      {special.image_url && (
        <img src={special.image_url} alt="" className="w-full h-36 object-cover rounded-xl mb-4"
          onError={(e) => { e.target.style.display = "none"; }} />
      )}
      {dishes.length > 0 && (
        <div className="mb-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700 mb-2">Menu items</h3>
          <div className="flex flex-wrap gap-1.5">
            {dishes.map((m, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-rose-100 text-rose-900 rounded-lg">{m}</span>
            ))}
          </div>
        </div>
      )}

      <div className="inline-flex bg-rose-100 rounded-full p-1 mb-5">
        {[
          { key: "orders", label: "Orders by day" },
          { key: "setup", label: "Setup" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`special-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${tab === t.key ? "bg-rose-700 text-white" : "text-rose-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="border border-rose-200 rounded-xl overflow-hidden">
          <div className="bg-rose-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-rose-900 flex items-center justify-between">
            <span>{special.scheduled_date}</span>
            <span>{special.booked || 0} booking{(special.booked || 0) !== 1 ? "s" : ""}</span>
          </div>
          <div className="p-4 text-center text-sm text-rose-700">
            <strong>{special.booked || 0}</strong> bookings out of <strong>{special.capacity || "∞"}</strong> capacity.
            <div className="text-xs text-rose-600 mt-1">Per-student booking list lives in wallet transactions (ref-order = special id).</div>
          </div>
        </div>
      )}

      {tab === "setup" && (
        <div className="space-y-3 text-sm">
          <div><strong>Slot:</strong> <span className="capitalize">{special.meal_slot || special.slot || "—"}</span></div>
          <div><strong>Price:</strong> {inr(special.price || 0)}</div>
          <div><strong>Scheduled date:</strong> {special.scheduled_date}</div>
          <div><strong>Order cutoff:</strong> {special.display_until || special.scheduled_date}</div>
          <div><strong>Capacity:</strong> {special.capacity || "Unlimited"}</div>
          <div><strong>Description:</strong> {special.description || "—"}</div>
        </div>
      )}
    </SidePanel>
  );
}

export default function SpecialsTab({ onCreate, onEdit, onPlanUpdated, showArchived }) {
  const [specials, setSpecials] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    const qp = showArchived ? "?includeArchived=true" : "";
    api.get(`/specials${qp}`)
      .then(r => setSpecials(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [showArchived]);

  const list = useMemo(() => {
    const t = today();
    if (showArchived) return specials;
    return specials.filter(s => !s.scheduled_date || s.scheduled_date >= t);
  }, [specials, showArchived]);

  if (loading) return <div className="text-sm text-rose-700">Loading…</div>;

  if (list.length === 0) {
    return (
      <div className="border-2 border-dashed border-rose-200 rounded-2xl p-12 text-center bg-rose-50/50">
        <h3 className="font-heading text-xl font-bold text-rose-900 mb-2">{showArchived ? "No archived special events" : "No upcoming special events"}</h3>
        <p className="text-sm text-rose-700 mb-4">{showArchived ? "Past events auto-archive after their cutoff date." : "Special events are one-shot menus (festivals, CSR, etc.) with an expiry datetime."}</p>
        {!showArchived && (
          <button onClick={() => onCreate("special")} data-testid="empty-create-special"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rose-700 text-white font-semibold hover:bg-rose-800">
            <Plus size={14} /> Create Special Event
          </button>
        )}
      </div>
    );
  }

  const handleChanged = () => { reload(); onPlanUpdated?.(); };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {list.map(s => (
          <SpecialCard key={s.id} special={s} onClick={() => setSelected(s)} onEdit={onEdit} onChanged={handleChanged} />
        ))}
      </div>
      {selected && <SpecialDetail special={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
